import { browserLanguages, resolveLocale } from "./locales";
import type { Locale } from "./locales";

/**
 * Region used when the browser offers none for the picked language. It is a
 * market choice, not a translation one — `Accept-Language` also feeds
 * `user.country` backend-side (IAP products, promotions, catalog filtering).
 */
export const API_LOCALE_FALLBACK: Record<Locale, string> = {
  en: "en-US",
  fr: "fr-FR",
  es: "es-ES",
  "pt-BR": "pt-BR",
  de: "de-DE",
  it: "it-IT",
  pl: "pl-PL",
  ru: "ru-RU",
  tr: "tr-TR",
};

const REGIONAL = /^([a-z]{2})-([a-z]{2})$/i;

/**
 * Map the active locale to the `xx-XX` the API expects, keeping the browser's
 * own region when it already agrees on the language: an `es-MX` visitor picking
 * Spanish stays on the MX market rather than being moved to `es-ES`.
 *
 * Takes the raw `i18n.resolvedLanguage`, narrowed by `resolveLocale`, so the
 * transport layer stays out of the locale vocabulary.
 */
export const resolveApiLocale = (
  active: string | undefined,
  languages: ReadonlyArray<string> = browserLanguages(),
): string => {
  const locale = resolveLocale(active);

  // An already regionalized locale (`pt-BR`) never matches a 2-letter language
  // and falls through to its own fallback entry.
  for (const language of languages) {
    const parts = REGIONAL.exec(language);
    if (parts && parts[1].toLowerCase() === locale)
      return `${locale}-${parts[2].toUpperCase()}`;
  }

  return API_LOCALE_FALLBACK[locale];
};
